import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Suspense } from "react";
import { stripeStatusQuery } from "@/api/billing";
import { dashboardSummaryQuery, upcomingAppointmentsQuery } from "@/api/queries";
import { warmQueries } from "@/api/warm";
import { useAuth } from "@/auth/AuthProvider";
import { AlertList } from "@/components/dashboard/AlertList";
import { DashboardSkeleton } from "@/components/dashboard/DashboardSkeleton";
import { SummaryCards } from "@/components/dashboard/SummaryCards";
import { UpcomingAppointments } from "@/components/dashboard/UpcomingAppointments";
import { ErrorBoundary } from "@/components/ErrorBoundary";

export const Route = createFileRoute("/_authed/")({
  loader: ({ context: { queryClient, establishmentId } }) =>
    warmQueries(queryClient, [
      dashboardSummaryQuery(),
      stripeStatusQuery(establishmentId),
      upcomingAppointmentsQuery(1),
    ]),
  staticData: {
    title: "Início",
    subtitle: "Resumo do dia e pendências que precisam de ação.",
  },
  component: InicioPage,
});

function InicioPage() {
  const { user } = useAuth();
  // `_authed` só renderiza autenticado — user nunca é null aqui.
  if (!user) return null;
  // Título e subtítulo vivem no cabeçalho do layout (`staticData`), então o
  // que sobra aqui é só o miolo. A fronteira de erro fica DENTRO dele: sem
  // ela, um 500 no resumo (ou no status da Stripe) subia até o roteador e
  // trocava o painel INTEIRO por um "Something went wrong!" em inglês, sem
  // sidebar e sem volta a não ser recarregar — logo na primeira tela de quem
  // entra. Assim o estrago cabe no miolo, com "Tentar de novo", e o cabeçalho
  // e a navegação seguem de pé.
  return (
    // Coluna única compartilhada pelos três estados (esqueleto, conteúdo e
    // erro) — assim a transição entre eles não desloca nada.
    <div className="mx-auto max-w-4xl space-y-6 p-4 sm:p-6">
      <ErrorBoundary titulo="Não foi possível carregar o resumo do dia">
        <Suspense fallback={<DashboardSkeleton />}>
          <DashboardContent establishmentId={user.establishment_id} />
        </Suspense>
      </ErrorBoundary>
    </div>
  );
}

function DashboardContent({ establishmentId }: { establishmentId: string }) {
  const { data: summary } = useSuspenseQuery(dashboardSummaryQuery());
  // `null` = Stripe nunca conectada: estado inicial normal, não vira alerta.
  const { data: stripe } = useSuspenseQuery(stripeStatusQuery(establishmentId));

  return (
    <>
      <SummaryCards summary={summary} />
      <AlertList summary={summary} stripeStatus={stripe?.status ?? null} />
      <UpcomingAppointments establishmentId={establishmentId} />
    </>
  );
}
